import { Map as MapIcon, MapPin } from 'lucide-react'
import type { EventPhase } from '@/lib/watch/rules'
import { StackedGlyph, WaitingState } from './StateGlyph'
import './watch.css'

/**
 * What stands in for the course map while it has nothing to frame: no course, and no located camera showing (a
 * live-only event's located cameras all offline). The page keeps the map mounted and hidden under it. A located camera
 * that goes live brings the map back, so the pin hops on the stacked disc (waiting) and the plain words say "yet": "No
 * pins yet", led for screen readers by "No camera is on the map yet". In the swap card the pin alone, still, beside the
 * full-size picture (the card's map is hidden from assistive tech). Full size, an h3 in the tone of what it sits on:
 * the canvas's ink on the desktop, the page's white in the phone's Map tab.
 *
 * The pin rests, with the same words, once the event's scheduled end has passed (`phase` `after`, when the canvas
 * says "All finished" and its mark settles) and wherever `still` asks (the phone's Map tab, under the canvas's moving
 * wait, as the Cameras grid holds still).
 */
export function MapWaiting({
  inCard,
  tone,
  phase = 'before',
  still = false,
}: {
  inCard: boolean
  tone: 'default' | 'inverse'
  phase?: EventPhase
  still?: boolean
}) {
  const hops = !still && phase !== 'after'
  return (
    <div className="watch-map__blank">
      {inCard ? (
        <StackedGlyph icon={<MapPin />} />
      ) : (
        <WaitingState
          tone={tone}
          art={<StackedGlyph icon={<MapPin />} motion={hops ? 'hop' : undefined} />}
          title="No pins yet"
          plain="No camera is on the map yet"
        />
      )}
    </div>
  )
}

/**
 * What stands over the full-size map until it has something drawn (CourseMap's `onLoadedChange`: the play world, or the
 * Mapbox map while no world is on its way), founder, 23 October 2026: the map is the default view, "with the loading
 * indicator if it's not been loaded". The folded map on the stacked disc, waking (fading up) as a camera's picture does
 * while it is on its way, over "Loading the map", a status. In the tone of what it sits on: the canvas's ink on the
 * desktop, the page's white in the phone's Map tab. Never in the swap card.
 */
export function MapLoading({ tone }: { tone: 'default' | 'inverse' }) {
  return (
    <div className="watch-map__blank">
      <WaitingState tone={tone} role="status" art={<StackedGlyph icon={<MapIcon />} motion="wake" />} title="Loading the map" />
    </div>
  )
}

/**
 * The phone's Map tab when the event has nothing to map at all (no course, no located camera): the folded map on the
 * stacked disc, still, as nothing will appear without a reload. An h3, as CourseMap's own Map unavailable.
 */
export function NoCourseMap() {
  return (
    <div className="watch-map__empty">
      <WaitingState art={<StackedGlyph icon={<MapIcon />} />} title="No course map" />
    </div>
  )
}
